import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import { useFirestore } from '../../firebase/useFirestore'
import useLocationFilter from '../../hooks/useLocationFilter'
import PersonasStoriesVisualization from '../dataVisualizations/PersonasStoriesVisualization'
import StaticCardLabel from './StaticCardLabel'
import DashboardFilterBar from './DashboardFilterBar'
import DashboardLocationFilter from './DashboardLocationFilter'
import DashboardCardEmptyState from './DashboardCardEmptyState'
import { FILTER_EMPTY_STATE } from './dashboardEmptyStateConfig'
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined'
import usePersonaLocations from '../../hooks/usePersonaLocations'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Dashboard wrapper around PersonasStoriesVisualization. Owns the data
// hooks + location filter + sort; the visualisation does the rendering.

const MAX_ITEMS = 10

const DashboardPersonaReportsCard = ({ item }) => {
    const history = useHistory()

    const reports = useFirestore('PersonaReports')
    const personas = usePersonaLocations()
    const users = useFirestore('Users')
    const { filteredItems, selectedLocationId, setSelectedLocationId } =
        useLocationFilter(personas, { item })

    const personaById = useMemo(() => {
        const map = {}
        filteredItems.forEach((p) => {
            if (p?.ID) map[p.ID] = p
        })
        return map
    }, [filteredItems])

    const allowedPersonaIds = useMemo(
        () => new Set(filteredItems.map((p) => p?.ID).filter(Boolean)),
        [filteredItems]
    )

    const userById = useMemo(() => {
        const map = {}
        ;(Array.isArray(users) ? users : []).forEach((u) => {
            if (u?.ID) map[u.ID] = u
        })
        return map
    }, [users])

    const latest = useMemo(() => {
        const list = Array.isArray(reports) ? [...reports] : []
        const filtered = selectedLocationId
            ? list.filter((r) => r?.PersonaID && allowedPersonaIds.has(r.PersonaID))
            : list
        filtered.sort((a, b) => {
            const ta = a?.Timestamp?.toDate ? a.Timestamp.toDate().getTime() : 0
            const tb = b?.Timestamp?.toDate ? b.Timestamp.toDate().getTime() : 0
            return tb - ta
        })
        return filtered
    }, [reports, selectedLocationId, allowedPersonaIds])

    const openReport = (report) => {
        history.push(`/${client}/personareportdetail/${report.PersonaID}/${report.ID}`)
    }

    const totalReports = Array.isArray(reports) ? reports.length : 0
    const isEmpty = latest.length === 0
    const isFilterEmpty = isEmpty && !!selectedLocationId && totalReports > 0

    return (
        <div>
            <StaticCardLabel
                title='Laatste rapporten'
                pill={{ variant: 'personas', label: 'Medewerkers' }}
            />
            <DashboardFilterBar
                onClear={() => setSelectedLocationId('')}
                activeFilterCount={selectedLocationId ? 1 : 0}
            >
                <DashboardLocationFilter
                    selectedLocationId={selectedLocationId}
                    onChange={setSelectedLocationId}
                />
            </DashboardFilterBar>

            {isFilterEmpty ? (
                <DashboardCardEmptyState
                    {...FILTER_EMPTY_STATE}
                    onCtaClick={() => setSelectedLocationId('')}
                />
            ) : isEmpty ? (
                <DashboardCardEmptyState type='personas-reports' />
            ) : (
                <PersonasStoriesVisualization
                    reports={latest}
                    personaById={personaById}
                    userById={userById}
                    maxItems={MAX_ITEMS}
                    onItemClick={openReport}
                />
            )}
        </div>
    )
}

export default DashboardPersonaReportsCard
